import { ReactNode, useLayoutEffect, useRef } from 'react';

const MaxLabelWidthEstimate = 22;
const LabelHeightEstimate = 9;
const MaxLabelAdjustment = 16;

/**
 * A helper component to ensure that value labels do not overlap.
 * It processes the children and adjusts their positions to avoid overlap.
 * Label positions are only moved upwards in case of overlap.
 * Total chart width is used to avoid everything going flying when the chart is too narrow.
 */
export const NonOverlappingValueLabels = (props: { children: ReactNode }) => {
  const containerRef = useRef<SVGGElement>(null);
  const labelWidthEstimate = MaxLabelWidthEstimate;
  useLayoutEffect(() => {
    if (!containerRef.current) {
      return;
    }
    const children = Array.from(containerRef.current.children);
    // We process labels from bottom to top
    const sortedLabels = children.sort((a, b) => {
      /* eslint-disable curly */
      const ay = parseFloat(a.getAttribute('y') ?? '0') + parseFloat(a.getAttribute('dy') ?? '0');
      const by = parseFloat(b.getAttribute('y') ?? '0') + parseFloat(b.getAttribute('dy') ?? '0');
      if (ay < by) return 1;
      if (ay > by) return -1;
      return 0;
    });

    const pos = sortedLabels.map((child) => {
      const dy = parseFloat(
        child.getAttribute('data-original-dy') ?? child.getAttribute('dy') ?? '0',
      );
      return {
        t: child.textContent,
        child,
        x: parseFloat(child.getAttribute('x') ?? '0'),
        dx: parseFloat(child.getAttribute('dx') ?? '0'),
        y: parseFloat(child.getAttribute('y') ?? '0'),
        dy,
        originalDy: dy,
      };
    });

    for (let i = 0; i < sortedLabels.length; i++) {
      const a = pos[i];
      for (let j = i + 1; j < sortedLabels.length; j++) {
        const b = pos[j];
        if (b.y + b.originalDy < a.y + a.dy - LabelHeightEstimate) {
          // Optimization: since the labels are sorted by y and the next label's original y is already out of range
          // and labels can only move up, then none of the remaining labels can overlap
          break;
        }
        if (
          Math.abs(a.x + a.dx - (b.x + b.dx)) < labelWidthEstimate &&
          b.y + b.dy > a.y + a.dy - LabelHeightEstimate
        ) {
          const minDy = b.originalDy - MaxLabelAdjustment;
          b.dy = Math.max(minDy, a.y + a.dy - b.y - LabelHeightEstimate);
        }
      }
    }
    for (let i = 0; i < sortedLabels.length; i++) {
      sortedLabels[i].setAttribute('data-original-dy', pos[i].originalDy.toString());
      sortedLabels[i].setAttribute('dy', pos[i].dy.toString());
    }
  });
  return <g ref={containerRef}>{props.children}</g>;
};
